import type { QuestionnaireItem } from '@medplum/fhirtypes'
import { questionnaireAnswerKey } from '@seen/core/models/compositionNote'
import { cn } from '@seen/ui'
import type { JSX, PointerEvent as ReactPointerEvent } from 'react'
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { answerValueForItem } from '#src/components/care-plan/answerAccept.ts'
import { proposalCaretOffsetFromPoint } from '#src/components/care-plan/answerCaretOffset.ts'
import {
  type AnswerSegment,
  type AnswerSegmentKind,
  appendedHtml,
  diffAnswerHtml,
  toAnswerLines,
} from '#src/components/care-plan/answerDelta.ts'
import {
  answerHeadingLines,
  findLabelLine,
  labelLineHead,
  labelLineValue,
  toInlineValue,
} from '#src/components/care-plan/answerLabelLines.ts'
import {
  AI_PROPOSAL_ADDITION_CLASS,
  AI_PROPOSAL_CARD_CLASS,
  AI_PROPOSAL_REMOVAL_CLASS,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import {
  type IslandActions,
  IslandActionsProvider,
} from '#src/components/rich-text-editor/IslandActionsContext.tsx'
import { IslandEditor } from '#src/components/rich-text-editor/IslandEditor.tsx'
import {
  createIslandRegistry,
  type IslandRegistry,
} from '#src/components/rich-text-editor/islandRegistry.ts'
import { SuggestionActionsToolbar } from '#src/components/rich-text-editor/SuggestionActionsToolbar.tsx'
import { SuggestionSourceExcerpt } from '#src/components/rich-text-editor/SuggestionSourceExcerpt.tsx'
import { RichText } from '#src/components/RichText.tsx'
import type {
  AnswerLineEdit,
  SuggestedAnswer,
} from '#src/hooks/useCarePlanQuestionnaireSuggestion.ts'

/* The card an open island sits in. The island tints itself for the note
 * editor, where its card is padded by a hair and the two fills read as one; this
 * card's padding would show that tint as a lighter rectangle inset in the
 * green, so the island here takes the card's fill instead. */
const ISLAND_CARD_CLASS = cn(
  AI_PROPOSAL_CARD_CLASS,
  '[&_.ai-proposal-island]:bg-transparent dark:[&_.ai-proposal-island]:bg-transparent'
)

// Consecutive segments of one kind, so a run renders through a single editor
// rather than one per line.
interface SegmentRun {
  kind: AnswerSegmentKind
  html: string
}

function toRuns(segments: AnswerSegment[]): SegmentRun[] {
  const runs: SegmentRun[] = []
  for (const segment of segments) {
    if (!segment.html.trim()) {
      continue
    }
    const open = runs.at(-1)
    if (open?.kind === segment.kind) {
      open.html = `${open.html}<br>${segment.html}`
      continue
    }
    runs.push({ kind: segment.kind, html: segment.html })
  }
  return runs
}

/* One rendered line of the item's answer: the line as stored, and the atom
 * proposing a value for it when one is still undecided. An atom with no line of
 * its own renders after the lines that do exist. */
interface AnswerRowBase {
  key: string
  lineHtml: string
  /* Came out of a heading block, so it is a block title and reads at the tier
   * an item's own name does. The line split drops the `<h4>` it was stored in,
   * so nothing in `lineHtml` says so. Never true of an atom's row: a label
   * search skips heading lines, and a trailing atom has no stored line at
   * all. */
  heading: boolean
}

/* A line no atom targets carries the index it sits at, which is how a hand edit
 * of it addresses one line of the stored answer. An atom's row has no use for
 * one: an accept writes through the atom's label, and a trailing atom has no
 * line to be indexed. */
interface AnswerLineRow extends AnswerRowBase {
  answer: null
  lineIndex: number
}

interface AnswerAtomRow extends AnswerRowBase {
  answer: SuggestedAnswer
}

type AnswerRow = AnswerLineRow | AnswerAtomRow

/* The item's answer split into rows, each pending atom attached to the line it
 * targets.
 *
 * Rendering only the cards would hide every line no atom targets, including a
 * value the clinician just accepted: that value returns to the answer as
 * ordinary text, and with the field itself replaced by this surface it would
 * have nowhere to appear until the item's last atom was decided. The bold
 * sub-headings are lines too, and they are what tells three `Goal:` proposals
 * apart now that no card captions itself. */
function toAnswerRows(
  currentAnswerHtml: string,
  answers: readonly SuggestedAnswer[]
): AnswerRow[] {
  const placed = new Map<number, SuggestedAnswer>()
  /* The atoms that get no line of their own, rendered after the stored lines.
   * An unlabelled atom proposes the WHOLE answer, so it is here for layout
   * alone. A labelled one is here only while the answer has not loaded and
   * offers no lines yet — when nothing can be accepted either. Once it has,
   * the questionnaire lists every labelled atom without a free line of its own
   * with the suggestions that couldn't be placed, and never hands it here. */
  const trailing: SuggestedAnswer[] = []
  for (const answer of answers) {
    if (!answer.label) {
      trailing.push(answer)
      continue
    }
    const index = findLabelLine(currentAnswerHtml, answer.label, answer.section)
    if (index === null || placed.has(index)) {
      trailing.push(answer)
      continue
    }
    placed.set(index, answer)
  }
  /* A free-prose item's atom proposes the WHOLE answer rather than one line of
   * it, so the stored lines are what its card already shows as replaced text —
   * listing them beside it would render the answer twice. */
  const labelled = answers.some((answer) => !!answer.label)
  const lines = labelled ? toAnswerLines(currentAnswerHtml) : []
  const headings = labelled ? answerHeadingLines(currentAnswerHtml) : []
  const rows: AnswerRow[] = []
  for (const [index, lineHtml] of lines.entries()) {
    const answer = placed.get(index) ?? null
    if (answer === null && !lineHtml.trim()) {
      continue
    }
    const heading = headings[index] ?? false
    rows.push(
      answer === null
        ? {
            key: `line-${index}`,
            lineHtml,
            answer,
            lineIndex: index,
            heading,
          }
        : {
            key: questionnaireAnswerKey(answer),
            lineHtml,
            answer,
            heading,
          }
    )
  }
  for (const answer of trailing) {
    rows.push({
      key: questionnaireAnswerKey(answer),
      lineHtml: '',
      answer,
      heading: false,
    })
  }
  return rows
}

/* One item's answer, rendered in the field's own place rather than in a
 * separate panel — so the clinician reads the answer once, with the suggested
 * text marked, instead of comparing a card against the field below it.
 *
 * The WHOLE answer renders, not just the proposals: a line no atom targets is
 * its own plain rich text, and a line an atom targets is its label followed by
 * that atom's card. One item carries several atoms, because a scaffold
 * answer holds several labelled lines and the AI proposes each separately, and
 * each is its own card with its own island and its own decision: opening or
 * accepting one leaves its siblings, and every other line, exactly as they
 * were.
 *
 * The three affordances are the note editor's, on the note editor's components:
 * Accept/Reject appear on hover, clicking a card opens an editable island at
 * the clicked position, and the island's own toolbar offers Accept with edits /
 * Restore original / Reject. What differs is only where an accept lands — a
 * PATCH to the QuestionnaireResponse rather than an insert into the shared
 * document. */
export function CarePlanQuestionnaireAnswerSuggestion({
  item,
  answers,
  currentValue,
  canAccept,
  onAccept,
  onReject,
  onEditLine,
}: {
  item: QuestionnaireItem
  answers: readonly SuggestedAnswer[]
  currentValue: unknown
  canAccept: boolean
  // Accepts the island's edited HTML when there is one, else the AI's answer.
  onAccept: (answer: SuggestedAnswer, editedHtml?: string) => void
  onReject: (answer: SuggestedAnswer) => void
  // Writes one line the clinician edited by hand.
  onEditLine: (edit: AnswerLineEdit) => void
}): JSX.Element {
  const currentAnswerHtml = typeof currentValue === 'string' ? currentValue : ''
  const rows = useMemo(
    () => toAnswerRows(currentAnswerHtml, answers),
    [currentAnswerHtml, answers]
  )

  return (
    <div className="flex flex-col gap-2 pb-2 text-sm">
      {/* Matches the item title a decided item renders through its form field,
          so a pending item does not shrink its own heading. */}
      <span className="text-base font-semibold">{item.text}</span>
      <div className="flex flex-col gap-0.5">
        {/* Paragraphs of a stored answer carry no margin anywhere else in the
            product — the same scaffold rendered by a decided item's form field
            stacks its labelled lines flush. A block start therefore opens no
            gap here either, or an item would visibly loosen just by having a
            suggestion on it. */}
        {rows.map((row) => (
          /* A heading stored inside the answer is a block title, and takes the
             same tier as the item's own name above it — the size the item's own
             form field gives that `<h4>` when nothing is pending on it. Without
             this an item loses its internal structure precisely when the AI has
             proposed something into it. */
          <div
            className={cn(row.heading && 'font-semibold text-base')}
            data-heading={row.heading ? 'true' : undefined}
            key={row.key}
          >
            {row.answer === null ? (
              <CarePlanQuestionnaireAnswerLine
                // The response is what a line is written to, so a line is only
                // editable once there is one — the same gate as accepting.
                editable={canAccept}
                lineHtml={row.lineHtml}
                lineIndex={row.lineIndex}
                onEdit={onEditLine}
              />
            ) : (
              <CarePlanQuestionnaireAnswerAtom
                answer={row.answer}
                canAccept={canAccept}
                currentAnswerHtml={currentAnswerHtml}
                item={item}
                onAccept={(editedHtml) => onAccept(row.answer, editedHtml)}
                onReject={() => onReject(row.answer)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/* One atomic edit — a single scaffold label's value, or the whole answer when
 * the item is free prose. Every piece of decision state lives here rather than
 * on the item: the island registry, the clinician's in-progress edit, and the
 * Accept/Reject calls all belong to this atom alone. */
function CarePlanQuestionnaireAnswerAtom({
  answer,
  currentAnswerHtml,
  canAccept,
  item,
  onAccept,
  onReject,
}: {
  answer: SuggestedAnswer
  // The item's whole stored answer. A labelled atom diffs only its own line of
  // it, so it is narrowed here rather than by the caller.
  currentAnswerHtml: string
  canAccept: boolean
  item: QuestionnaireItem
  onAccept: (editedHtml?: string) => void
  onReject: () => void
}): JSX.Element {
  /* Picked by the item's declared type rather than by whichever field the model
   * filled, so the card previews exactly what an accept would write: an answer
   * carrying a stray boolean beside its text would otherwise preview as Yes/No
   * and then write the text. */
  const value = answerValueForItem(answer, item)

  /* A boolean answer is a Yes/No with nothing to edit, so it gets the hover
   * decision but no island — the same reason validated-tool proposals never
   * carried a decoration. */
  const isBoolean = typeof value === 'boolean'

  /* The line's label, rendered once in front of the proposal so the line reads
   * as one label and one value. Taken from the answer, whose spelling is the
   * one already on screen, falling back to the atom's own spelling.
   *
   * Rendered bold, because the scaffolds bold their labels and an accept keeps
   * that bold: the card is a preview of the line it would write, so a plain
   * label here would restyle itself the moment it was accepted. */
  const labelHead = useMemo(
    () =>
      answer.label
        ? (labelLineHead(currentAnswerHtml, answer.label, answer.section) ??
          answer.label)
        : null,
    [answer.label, answer.section, currentAnswerHtml]
  )

  /* A fill-only proposal that keeps its line's value and adds to the end of it:
   * the value is the clinician's existing text, not part of the suggestion, so
   * it renders as the ordinary line it is and only the addition is proposed —
   * and the island edits the addition alone. An accept still writes the whole
   * line, the existing value followed by the addition. */
  const append = useMemo(() => {
    if (
      answer.fillOnly !== true ||
      !answer.label ||
      typeof value !== 'string'
    ) {
      return null
    }
    const index = findLabelLine(currentAnswerHtml, answer.label, answer.section)
    const line =
      index === null ? undefined : toAnswerLines(currentAnswerHtml)[index]
    const base =
      labelLineValue(currentAnswerHtml, answer.label, answer.section) ?? ''
    const addition = appendedHtml(base, value)
    return line === undefined || addition === null
      ? null
      : { line, base, addition }
  }, [answer.fillOnly, answer.label, answer.section, currentAnswerHtml, value])

  /* What an island edit writes. On an append the island holds the addition
   * alone, so the line's existing value goes back in front of it — flattened
   * first, since the island's document is always block-wrapped and the value
   * is one inline line. */
  const lineFromEdit = useCallback(
    (edited: string): string =>
      append ? `${append.base}${toInlineValue(edited)}` : edited,
    [append]
  )

  /* The clinician's island edit, held only in this component. The staging
   * record has no per-atom edited-content field, and an edit is meaningful only
   * until the atom is decided — accepting writes it to the response and
   * rejecting discards it, so there is nothing left for a persisted edit to
   * serve. */
  const [editedHtml, setEditedHtml] = useState<string | null>(null)

  const registry = useMemo(() => createIslandRegistry(), [])
  // One island per card, so its id only has to be stable, not globally unique.
  const islandId = questionnaireAnswerKey(answer)
  const editing = useSyncExternalStore(registry.subscribe, () =>
    registry.getOpenSnapshot(islandId)
  )

  /* Snapshotted as the island opens rather than read live: `persistEdited`
   * fires on a debounce WHILE the clinician types, and feeding that back in as
   * a prop would change the island's seed under its own editor. */
  const openSeedRef = useRef<string | null>(null)

  const actions = useMemo<IslandActions>(
    () => ({
      acceptWithEdits: (_id, editedContent) =>
        onAccept(lineFromEdit(editedContent)),
      reject: () => onReject(),
      persistEdited: (_id, editedContent) => setEditedHtml(editedContent),
      clearEdited: () => setEditedHtml(null),
      /* The care plan's questionnaire is not inside the note document, so
       * there is no host history to hand a keystroke back to and no host caret
       * to exit to. Refusing all three leaves the island's own history and
       * cursor handling in charge, which is the whole surface here. */
      hostUndo: () => false,
      hostRedo: () => false,
      exitToHost: () => false,
    }),
    [onAccept, onReject, lineFromEdit]
  )

  // What an accept would write: the clinician's edit if they made one.
  const proposedHtml = editedHtml ?? (typeof value === 'string' ? value : '')

  const runs = useMemo(() => {
    if (typeof value === 'boolean') {
      return [
        {
          kind: 'added' as const,
          html: value ? 'Yes' : 'No',
        },
      ]
    }
    /* A labelled atom proposes one line's VALUE, so it is the current value of
     * that line — not the whole answer — that the proposal replaces. A label the
     * stored answer does not carry yet has no current value, and the proposal
     * reads as wholly new.
     *
     * The card must diff its OWN line: without `section`, three cards for a
     * repeated label would all diff against the first occurrence. */
    const currentHtml = answer.label
      ? (labelLineValue(currentAnswerHtml, answer.label, answer.section) ?? '')
      : currentAnswerHtml
    return toRuns(diffAnswerHtml(currentHtml, proposedHtml))
  }, [answer.label, answer.section, currentAnswerHtml, proposedHtml, value])

  const contentRef = useRef<HTMLDivElement>(null)

  /* Where the click that opens the island landed, resolved on POINTERDOWN: an
   * island open elsewhere dismisses itself from a document-level pointerdown,
   * and that unmount reflows the answer, so by click time the same coordinates
   * address different text. Cleared as it is consumed, so a keyboard open —
   * which has no click point at all — falls back to the island's end. */
  const pressedCaretRef = useRef<number | undefined>(undefined)

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ): void => {
    const content = contentRef.current
    if (content === null || event.button !== 0) {
      pressedCaretRef.current = undefined
      return
    }
    const offset = proposalCaretOffsetFromPoint(
      content,
      event.clientX,
      event.clientY
    )
    /* An offset into the island document's first text block. Position 0 is the
     * document itself, which Tiptap's autofocus resolution reads as no position
     * at all, so the first character is 1. */
    pressedCaretRef.current = offset === null ? undefined : offset + 1
  }

  const openIsland = (): void => {
    openSeedRef.current = editedHtml
    const caretPos = pressedCaretRef.current
    pressedCaretRef.current = undefined
    registry.open(islandId, caretPos)
  }

  /* Reject stays live where Accept cannot: the atom is still a decision the
   * clinician has to clear, and leaving it undecided is what keeps the item
   * pending forever. */
  const toolbar = (
    <SuggestionActionsToolbar
      acceptDisabled={!canAccept}
      onAccept={() =>
        onAccept(editedHtml === null ? undefined : lineFromEdit(editedHtml))
      }
      onReject={onReject}
    />
  )

  /* A removal is text the answer still carries and the proposal would drop, so
   * it cannot sit inside a card that reads as the accepted line. It takes the
   * struck-through line above the card, which is how the note editor shows a
   * replacement. */
  const removedRuns = runs.filter((run) => run.kind === 'removed')
  const proposedRuns = runs.filter((run) => run.kind !== 'removed')

  /* One card holding the line's LABEL and the value proposed for it. The card is
   * block-level, so it spans the answer's full width from the left margin and
   * wraps to that same margin — the shape the accepted `Goal: value` will have.
   * A card holding the value alone starts after the label instead, indenting
   * every wrapped line to its own left edge, and an inline card avoids the
   * indent only by breaking into a ragged fragment per wrapped line. */
  const insertCard = (
    <div className={AI_PROPOSAL_CARD_CLASS}>
      {!!labelHead && <strong className="mr-1">{labelHead}</strong>}
      {proposedRuns.map((run, index) => (
        <AnswerRun
          first={index === 0}
          // Runs have no identity of their own; position is what distinguishes
          // them, and the list is rebuilt whenever the answer changes.
          key={`${run.kind}-${index}`}
          run={run}
        />
      ))}
    </div>
  )

  /* An append reads as the line itself: the stored line exactly as the answer
   * renders it, and the addition marked inline right after it, with nothing
   * between the two that the stored text does not hold. The stored line carries
   * no run marker, so a click on it opens the island where the addition
   * begins — the island does not hold the existing value. */
  const appendLine = append && (
    <div>
      <RichText inline value={append.line} />
      <span className={AI_PROPOSAL_ADDITION_CLASS} data-answer-run="added">
        <RichText
          inline
          value={
            editedHtml === null ? append.addition : toInlineValue(editedHtml)
          }
        />
      </span>
    </div>
  )

  // The atom's rendered delta: the text an accept would drop, then the card
  // holding the line it would write.
  const delta = appendLine || (
    <>
      {removedRuns.length > 0 && (
        <div>
          {removedRuns.map((run, index) => (
            <AnswerRun
              first={index === 0}
              key={`${run.kind}-${index}`}
              run={run}
            />
          ))}
        </div>
      )}
      {insertCard}
    </>
  )

  /* The atom's decision controls sit above the PROPOSAL — the struck-through
   * text and the card together — rather than above the item. Anchoring to the
   * item put the toolbar at the top of the questionnaire no matter which of its
   * atoms the clinician was reading. It stays outside the card so the card
   * reads as nothing but the line an accept would write. */
  const closed = (
    <div className="group/proposal relative">
      {isBoolean ? (
        delta
      ) : (
        /* The card's text is the affordance, as a suggestion's text is in the
         * note — there is no separate "edit" control to find. The replaced text
         * is inside the clickable region too, so clicking it opens the island
         * where the replacement begins rather than doing nothing. */
        // the clickable region is the rendered answer, which is rich text carrying paragraphs a <button> may not contain.
        <div
          className="cursor-text"
          onClick={openIsland}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              openIsland()
            }
          }}
          onPointerDown={handlePointerDown}
          ref={contentRef}
          role="button"
          tabIndex={0}
        >
          {delta}
        </div>
      )}
      {/* Outside the clickable region, not inside it: a click on Accept used to
          bubble into the open handler as well, so the island opened for as long
          as the accept took to land and the card visibly changed shape under
          the button that was being pressed. */}
      {/* Revealed by opacity rather than `hidden`, because a display:none
          toolbar takes its buttons out of the tab order entirely, and the
          atom could then never be decided without a mouse. Kept absolute, so
          holding it in the layout costs no space. */}
      <span className="pointer-events-none absolute right-0 bottom-full z-50 flex pb-1.5 opacity-0 transition-opacity group-focus-within/proposal:pointer-events-auto group-focus-within/proposal:opacity-100 group-hover/proposal:pointer-events-auto group-hover/proposal:opacity-100">
        {toolbar}
      </span>
    </div>
  )

  const island = (
    <IslandActionsProvider value={actions}>
      <IslandEditor
        editedContent={openSeedRef.current ?? undefined}
        id={islandId}
        // Where the click that opened this island landed, as the note
        // editor's widget forwards its own preview's mapped position.
        initialCaretPos={registry.getCaretPos(islandId)}
        // What Restore original returns to: the AI's proposal, which on an
        // append is the addition alone.
        originalHtml={append?.addition ?? answer.valueString ?? ''}
        registry={registry}
      />
    </IslandActionsProvider>
  )

  const proposal = (
    <>
      {editing &&
        (append ? (
          /* The existing value stays the plain line it is, and the card holds
           * only the addition the island edits. */
          <>
            <div>
              <RichText inline value={append.line} />
            </div>
            <div className={ISLAND_CARD_CLASS}>{island}</div>
          </>
        ) : (
          /* The island draws no box of its own — in the note editor the outline
           * comes from the proposal decoration it opens inside. This surface has
           * no decoration, so the shell is supplied here: the same card the
           * closed proposal wears, label included, so opening one changes only
           * what is inside the box. */
          <div className={ISLAND_CARD_CLASS}>
            {!!labelHead && <strong>{labelHead}</strong>}
            {island}
          </div>
        ))}
      {!editing && closed}
    </>
  )

  return (
    // Everything belonging to ONE atom — its struck-through text, its card, its
    // decision controls and its evidence. A query scoping to a single atom
    // anchors here rather than on a layout class, which spacing work would move.
    <div className="flex flex-col gap-0.5" data-answer-atom="">
      {proposal}
      <SuggestionSourceExcerpt
        citation={answer.citation}
        sourceProvenance={answer.sourceProvenance}
      />
    </div>
  )
}

/* One stored line no atom targets, editable in place.
 *
 * A single pending atom puts this surface in the field's place, so a read-only
 * line left the clinician unable to touch anything else on the item until every
 * atom was decided. Editing happens in the same island the cards open, seeded
 * with the line as stored, rather than by bringing the field back: the field
 * binds one debounced editor to the WHOLE answer, and an accept writes the whole
 * answer too, so the two would overwrite each other's text. One line, one
 * island, one writer at a time.
 *
 * The edit is committed as the island CLOSES, not while it is typed into. Each
 * write substitutes into the line this row was built from, so a write landing
 * mid-edit would rewrite the answer — and reindex its lines — under the island
 * still open on it. */
function CarePlanQuestionnaireAnswerLine({
  lineHtml,
  lineIndex,
  editable,
  onEdit,
}: {
  lineHtml: string
  lineIndex: number
  editable: boolean
  onEdit: (edit: AnswerLineEdit) => void
}): JSX.Element {
  // What the commit needs, read at close time rather than captured when the
  // island opened, so a row re-rendered against a changed answer commits
  // against the line it is showing now.
  const commitRef = useRef({ lineHtml, lineIndex, onEdit })
  commitRef.current = { lineHtml, lineIndex, onEdit }

  const registry = useMemo<IslandRegistry>(() => {
    const base = createIslandRegistry()
    return {
      ...base,
      /* Every way out of an island routes through close — a pointerdown
       * elsewhere, focus leaving — so the edit is read off the island's
       * controller here, while it still exists, rather than from a decision the
       * island would have to grow a control to offer. Guarded on the island
       * actually being open, because a click outside fires both paths. */
      close: (id) => {
        if (!base.isOpen(id)) {
          return
        }
        const html = base.getController(id)?.getCurrentContent()
        base.close(id)
        if (html === undefined) {
          return
        }
        const commit = commitRef.current
        commit.onEdit({
          index: commit.lineIndex,
          previousHtml: commit.lineHtml,
          html,
        })
      },
    }
  }, [])

  const islandId = `line-${lineIndex}`
  const editing = useSyncExternalStore(registry.subscribe, () =>
    registry.getOpenSnapshot(islandId)
  )

  const contentRef = useRef<HTMLDivElement>(null)
  const pressedCaretRef = useRef<number | undefined>(undefined)

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ): void => {
    const content = contentRef.current
    if (content === null || event.button !== 0) {
      pressedCaretRef.current = undefined
      return
    }
    const offset = proposalCaretOffsetFromPoint(
      content,
      event.clientX,
      event.clientY
    )
    pressedCaretRef.current = offset === null ? undefined : offset + 1
  }

  const openIsland = (): void => {
    const caretPos = pressedCaretRef.current
    pressedCaretRef.current = undefined
    registry.open(islandId, caretPos)
  }

  if (!editable) {
    return <RichText value={lineHtml} />
  }

  if (editing) {
    return (
      /* Null, and supplied EXPLICITLY: island actions come from React context,
       * and this questionnaire renders inside the care plan's note editor —
       * through a Tiptap node view, whose React subtree hangs off the host tree
       * and therefore inherits CollaborativeEditor's IslandActionsProvider. So a
       * line the AI proposed nothing for was reaching the NOTE's accept
       * pipeline, and wearing its tint and its three verbs. The island's
       * discriminator is the presence of actions; overriding them here is what
       * makes a plain line actually plain. */
      <IslandActionsProvider value={null}>
        <IslandEditor
          id={islandId}
          initialCaretPos={registry.getCaretPos(islandId)}
          // No proposal stands behind a stored line, so the island's "original"
          // is the line itself — what a restore would return it to.
          originalHtml={lineHtml}
          registry={registry}
        />
      </IslandActionsProvider>
    )
  }

  return (
    // the field is one line of a rich-text answer, which neither <input> nor <textarea> can hold.
    <div
      className="cursor-text"
      /* The whole line is the island's document, so every character of it counts
       * toward the offset a click resolves to — which is what this attribute
       * means to `proposalCaretOffsetFromPoint`. */
      data-answer-run="unchanged"
      onClick={openIsland}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          openIsland()
        }
      }}
      onPointerDown={handlePointerDown}
      ref={contentRef}
      role="textbox"
      tabIndex={0}
    >
      <RichText value={lineHtml} />
    </div>
  )
}

/* One run of an atom's delta. Only a removal marks itself: an added run is
 * already inside the insert card, which is what carries the proposal's colours
 * now. */
function AnswerRun({
  run,
  first,
}: {
  run: SegmentRun
  first: boolean
}): JSX.Element {
  return (
    <span
      className={cn(
        !first && 'ml-1',
        run.kind === 'removed' && AI_PROPOSAL_REMOVAL_CLASS
      )}
      // Read by proposalCaretOffsetFromPoint, which counts only proposed text:
      // neither the label nor the replaced text is in the island's document.
      data-answer-run={run.kind}
    >
      <RichText inline value={run.html} />
    </span>
  )
}
